import React, { useState, useEffect, useRef } from 'react';
import { Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import api from '../../utils/axios';

import { Webview } from '../Webview';
import { WebviewToolbar } from '../WebviewToolbar';

interface PreviewPanelProps {
  projectId: string;
  onClose: () => void;
}

interface ProjectUrls {
  id: string;
  project_id: string;
  url_lines: string[];
  last_updated_from_thread_id?: string;
  last_updated_from_message_id?: string;
  created_at: number;
  updated_at: number;
  user_id: string;
}

interface PortsResponse {
  project_id: string;
  ports: Record<string, { port: number; is_primary: boolean }>;
  message?: string;
}

const PreviewPanel: React.FC<PreviewPanelProps> = ({
  projectId,
  onClose,
}) => {
  const [initialUrl, setInitialUrl] = useState<string>('');
  const [currentUrl, setCurrentUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [noUrlAvailable, setNoUrlAvailable] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Extract URL from URL line (same logic as ApplicationUrlsModal)
  const extractUrlFromLine = (urlLine: string): string | null => {
    const colonIndex = urlLine.indexOf(':');
    if (colonIndex === -1) return null;
    
    const url = urlLine.substring(colonIndex + 1).trim();
    return url.startsWith('http') ? url : null;
  };


  // Fetch ports and application URLs on mount
  useEffect(() => {
    const fetchAndLoadUrl = async () => {
      try {
        setIsLoading(true);
        setNoUrlAvailable(false);

        // Fetch ports and application URLs in parallel
        const [portsResponse, urlsResponse] = await Promise.all([
          api.get<PortsResponse>(`/projects/${projectId}/ports`),
          api.get<ProjectUrls | null>(`/projects/${projectId}/urls`)
        ]);

        const ports = portsResponse.data.ports;
        const urls = urlsResponse.data;

        // Find the primary port from all ports
        let primaryPort: number | null = null;
        if (ports && Object.keys(ports).length > 0) {
          for (const [serviceType, portInfo] of Object.entries(ports)) {
            if (portInfo.is_primary) {
              primaryPort = portInfo.port;
              console.log(`Found primary port ${primaryPort} for service: ${serviceType}`);
              break;
            }
          }
        }

        // Check if we have both primary port and URLs
        if (!primaryPort) {
          console.log('No primary port found for project');
          setNoUrlAvailable(true);
          setIsLoading(false);
          return;
        }

        if (!urls || !urls.url_lines || urls.url_lines.length === 0) {
          console.log('No application URLs found for project');
          setNoUrlAvailable(true);
          setIsLoading(false);
          return;
        }

        // Parse URLs and find the one with primary port
        let foundUrl: string | null = null;
        for (const urlLine of urls.url_lines) {
          const url = extractUrlFromLine(urlLine);
          if (url && url.includes(`:${primaryPort}`)) {
            foundUrl = url;
            break;
          }
        }

        if (foundUrl) {
          console.log('Found URL with primary port:', foundUrl);
          setInitialUrl(foundUrl);
          setCurrentUrl(foundUrl);
          setNoUrlAvailable(false);
        } else {
          console.log('No URL found with primary port');
          setNoUrlAvailable(true);
        }

        setIsLoading(false);
       
      } catch (error: any) {
        console.error('Failed to fetch project data:', error);
        setNoUrlAvailable(true);
        setIsLoading(false);
      }
    };

    if (projectId) {
      fetchAndLoadUrl();
    }
  }, [projectId]);

  // Handle URL changes from toolbar
  const handleUrlChange = (url: string) => {
    setIsLoading(true);
    setCurrentUrl(url);
    setReloadKey(prev => prev + 1); // Force iframe reload
  };

  // Handle webview load complete
  const handleWebviewLoad = () => {
    console.log('✅ Webview loaded successfully');
    setIsLoading(false);
  };

  // Handle reload button click - force reload by incrementing key
  const handleReload = () => {
    setIsLoading(true);
    setReloadKey(prev => prev + 1); // Changing key forces React to remount the iframe
  };

  // Handle home button click
  const handleHome = () => {
    if (initialUrl) {
      setIsLoading(true);
      setCurrentUrl(initialUrl);
      setReloadKey(prev => prev + 1); // Force iframe reload
    }
  };

  // Simple commands object for toolbar
  const commands = {
    navigate: handleUrlChange,
    reload: handleReload,
    back: () => {}, // Not implemented
    forward: () => {}, // Not implemented
  };

  return (
    <div className="flex flex-col h-full w-full bg-bg-primary rounded-bg-v2-lg container-shadow-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between min-h-[60px] max-h-[60px] px-bg-v2-xl bg-bg-primary border-b border-border-secondary">
        <div className="flex items-center gap-bg-v2-lg flex-1">
          <Icon name={IconName.Layout} size="sm" variant="secondary" />
          <Text variant="text-sm" weight="semibold" color="primary">
            Preview
          </Text>
        </div>
        <button
          onClick={onClose}
          className="flex items-center justify-center w-6 h-6 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200 flex-shrink-0"
          aria-label="Close"
        >
          <Icon name={IconName.Cross} size="sm" inheritColor />
        </button>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 flex-col items-start overflow-hidden bg-bg-primary min-h-0">
        {/* Webview Toolbar - only show if we have a URL */}
        {!noUrlAvailable && (
          <WebviewToolbar
            currentUrl={currentUrl}
            canGoBack={false}
            canGoForward={false}
            isLoading={isLoading}
            commands={commands}
            initialUrl={initialUrl}
            onUrlChange={handleUrlChange}
          />
        )}

        {/* Webview Content or No URL Message */}
        <div className="flex-1 min-h-0 w-full relative overflow-hidden">
          {noUrlAvailable ? (
            <div className="flex flex-col items-center justify-center h-full w-full gap-bg-v2-lg px-bg-v2-xl">
              <Icon name={IconName.WebBrowser} size="xl" variant="utility-gray" />
              <div className="flex flex-col items-center gap-bg-v2-xs">
                <Text variant="text-md" weight="semibold" color="primary" align="center">
                  No URL Available
                </Text>
              </div>
            </div>
          ) : (
            <Webview
              key={reloadKey}
              ref={iframeRef}
              url={currentUrl}
              className="w-full h-full"
              onLoad={handleWebviewLoad}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PreviewPanel;

